/**
 * Target-pest filtering for the spray wizard's chemical step.
 *
 * A crop registration is one (chemical, situation, target pest) row, so the
 * pests worth offering for a crop are exactly the distinct `target_pest` values
 * among that situation's registrations — no separate pest list to drift out of
 * step with what is actually registered.
 *
 * The same pest arrives spelt more than one way: the APVMA import writes the
 * PUBCRIS description ("Black spot"), a hand-entered or label-read row often
 * carries the scientific name too ("Black spot (Venturia inaequalis)"). Those
 * have to collapse to one choice, or picking one silently hides the products
 * filed under the other.
 */

export interface PestRegistrationRow {
  id: number;
  target_pest?: string | null;
}

export interface TargetPestOption {
  key: string;
  label: string;
  /** Distinct products registered against it in this situation. */
  chemicalCount: number;
}

/** Case, spacing and a trailing parenthetical (the scientific name) ignored. */
export function pestKey(name: string): string {
  return name
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The pests a situation's registrations name, alphabetical. */
export function distinctPests(rows: PestRegistrationRow[]): TargetPestOption[] {
  const byKey = new Map<string, { label: string; chemicals: Set<number> }>();
  for (const row of rows) {
    if (!row.target_pest?.trim()) continue;
    const key = pestKey(row.target_pest);
    if (!key) continue;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { label: row.target_pest.trim(), chemicals: new Set() };
      byKey.set(key, entry);
    } else if (row.target_pest.trim().length < entry.label.length) {
      // Prefer the plain spelling over the one carrying the scientific name.
      entry.label = row.target_pest.trim();
    }
    entry.chemicals.add(row.id);
  }
  return [...byKey.entries()]
    .map(([key, e]) => ({ key, label: e.label, chemicalCount: e.chemicals.size }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Registrations with no target pest recorded — hidden while a pest filter is on. */
export function countUnnamedPests(rows: PestRegistrationRow[]): number {
  return rows.filter(r => !r.target_pest?.trim()).length;
}

/** Which of the selected pests each product is registered against. */
export function pestCoverage(
  rows: PestRegistrationRow[],
  selectedKeys: string[],
): Map<number, Set<string>> {
  const wanted = new Set(selectedKeys);
  const coverage = new Map<number, Set<string>>();
  for (const row of rows) {
    if (!row.target_pest) continue;
    const key = pestKey(row.target_pest);
    if (!wanted.has(key)) continue;
    if (!coverage.has(row.id)) coverage.set(row.id, new Set());
    coverage.get(row.id)!.add(key);
  }
  return coverage;
}

/**
 * Registrations for the selected pests. A product registered against more of
 * them sorts first — one product that handles black spot and powdery mildew is
 * one less thing in the tank. No selection means no filter.
 */
export function filterByPests<T extends PestRegistrationRow & { product_name: string }>(
  rows: T[],
  selectedKeys: string[],
): T[] {
  if (!selectedKeys.length) return rows;
  const wanted = new Set(selectedKeys);
  const coverage = pestCoverage(rows, selectedKeys);
  return rows
    .filter(r => r.target_pest && wanted.has(pestKey(r.target_pest)))
    .sort((a, b) =>
      (coverage.get(b.id)?.size ?? 0) - (coverage.get(a.id)?.size ?? 0)
      || a.product_name.localeCompare(b.product_name));
}

/**
 * Selected pests that no product in the mix is registered against. Judged on
 * every registration the product holds for the situation, not only the row that
 * was picked: a fungicide chosen for black spot may carry a separate powdery
 * mildew registration, and that still covers it.
 */
export function uncoveredPests(
  rows: PestRegistrationRow[],
  selectedKeys: string[],
  mixChemicalIds: number[],
): string[] {
  const coverage = pestCoverage(rows, selectedKeys);
  const covered = new Set<string>();
  for (const id of mixChemicalIds) {
    for (const key of coverage.get(id) ?? []) covered.add(key);
  }
  return selectedKeys.filter(k => !covered.has(k));
}
